import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Sidebar from "../Sidebar";
import axios from "axios";
import * as XLSX from "xlsx";
import { FaUsers } from "react-icons/fa6";
import { FaFileExcel } from "react-icons/fa6";
import TopBar from "../TopBar";
import { getDB, dbGet, dbPut } from "../../db";

import {
  TableRow,
  TableHeader,
  TableCell,
} from "../import/ImportComponentStyles";

/* ----------------- styled components ----------------- */

const Wrapper = styled.div`
  width: 100%;
  padding: 20px;
  height: 100vh;
  overflow-y: auto;
`;

const ScrollContainer = styled.div`
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #ccc;
  border-radius: 8px;
  background-color: #fff;
  font-size: 0.7rem;

  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid #eee;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }

  th {
    background-color: #87ceeb;
    font-weight: 600;
    color: #333;
    cursor: pointer;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  tr:hover {
    background-color: #eef6ff;
  }
`;

const PaginationControls = styled.div`
  margin-top: 10px;
  padding: 10px 0;
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;

  button {
    padding: 6px 12px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid #87ceeb;
    background: #87ceeb;
    color: #fff;
    border-radius: 4px;
    transition: background 0.2s ease;

    &:hover:not(:disabled) {
      background-color: #62b2d6;
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  span {
    align-self: center;
    font-size: 14px;
    font-weight: bold;
    color: #333;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px 0;
  font-size: 1.2rem;
  font-weight: bold;
  color: #87ceeb;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FilterBar = styled.div`
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 180px;

  label {
    font-weight: bold;
    margin-bottom: 5px;
  }

  input {
    padding: 6px;
    border-radius: 4px;
    border: 1px solid #ccc;
  }
`;

const ExportButton = styled.button`
  background-color: #217346;
  color: #fff;
  font-weight: bold;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  height: 30px;
  transition: background-color 0.2s;
  display: inline-flex;
  align-items: center;

  &:hover {
    background-color: #1a5d36;
  }
`;

/* ----------------- config / helpers ----------------- */

// Columns we never show in the UI / export
const HIDDEN_COLUMNS = ["id", "gus_created_at", "gus_last_updated", "source"];

const columnLabels = {
  nip: "NIP",
  nazwa: "Nazwa",
  wojewodztwo: "Województwo",
  powiat: "Powiat",
  gmina: "Gmina",
  miejscowosc: "Miejscowość",
  kodPocztowy: "Kod Pocztowy",
  ulica: "Ulica",
  nrNieruchomosci: "Nr Nieruchomości",
};

// Build visible columns from any set of rows
const buildColumns = (rows) => {
  if (!rows || !rows.length) return [];
  const colSet = new Set();
  rows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (!HIDDEN_COLUMNS.includes(key)) {
        colSet.add(key);
      }
    });
  });
  return Array.from(colSet);
};

// NIP normalization helper
const normalizeNip = (raw) => {
  if (!raw && raw !== 0) return "";
  let s = String(raw).trim().toUpperCase();
  s = s.replace(/^PL/, ""); // remove PL prefix
  s = s.replace(/[^0-9]/g, ""); // keep only digits
  return s;
};

const SALES_NIP_KEYS = [
  "NIP",
  "nip",
  "NIPNabywcy",
  "NabywcaNIP",
  "NrNabywcy",
  "NrKontrahenta",
  "NrDostawcy",
  "TIN",
  "KodNIP",
];

/* ----------------- component ----------------- */

const KlienciComponent = () => {
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [filters, setFilters] = useState({
    nip: "",
    nazwa: "",
    wojewodztwo: "",
  });
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 18;

  useEffect(() => {
    const loadData = async () => {
      try {
        await getDB();

        const user = JSON.parse(localStorage.getItem("user"));
        const rawRole = user?.Role ?? user?.role ?? "";
        const roleStr = String(rawRole).trim().toUpperCase();
        // SUPERADMIN, SUPERUSER, SUPER_* → full access
        const isSuperUser = roleStr.includes("SUPER");
        const isDatainBasicDemo =
          String(user?.Company ?? user?.company ?? "").trim().toLowerCase() === "datain" &&
          String(user?.ID ?? user?.id ?? "").trim().toUpperCase() === "BASIC_DEMO" &&
          String(user?.LicenseType ?? user?.licenseType ?? "").trim().toUpperCase() === "BASIC_DEMO";

        const now = Date.now();

        // ---- Clients: cached (1h) or fetch ----
        let clientsList = [];
        const cachedClients = await dbGet("clients", "allClients");
        if (!isDatainBasicDemo && cachedClients && now - cachedClients.timestamp < 60 * 60 * 1000) {
          clientsList = cachedClients.data || [];
        } else {
          const res = await axios.get("https://server229256.nazwa.pl/api/klienci");
          clientsList = res.data || [];
          await dbPut("clients", "allClients", {
            data: clientsList,
            timestamp: now,
          });
        }

        console.log("[Klienci] rola =", roleStr, "isSuperUser =", isSuperUser,
          "isDatainBasicDemo =", isDatainBasicDemo);
        console.log("[Klienci] liczba klientów =", clientsList.length);

        const baseColumns = buildColumns(clientsList);
        if (baseColumns.length && !columns.length) {
          setColumns(baseColumns);
        }

        // The datain BASIC_DEMO account can see its contractor list too.
        if (isSuperUser || isDatainBasicDemo) {
          setData(clientsList);
          if (baseColumns.length) setColumns(baseColumns);
          return;
        }

        // ---- USER branch: restrict to clients present in SALES ----

        // Sales: cached (1h) or fetch
        let salesData = [];
        const cachedSales = await dbGet("sales", "allSales");
        if (cachedSales && now - cachedSales.timestamp < 60 * 60 * 1000) {
          salesData = cachedSales.data || [];
        } else {
          const resSales = await axios.get(
            "https://server229256.nazwa.pl/api/sprzedaz"
          );
          salesData = Array.isArray(resSales.data) ? resSales.data : [];
          await dbPut("sales", "allSales", {
            data: salesData,
            timestamp: now,
          });
        }

        const nipsWithTransactions = new Set();
        for (const row of salesData) {
          for (const key of SALES_NIP_KEYS) {
            if (row && Object.prototype.hasOwnProperty.call(row, key)) {
              const norm = normalizeNip(row[key]);
              if (norm) nipsWithTransactions.add(norm);
            }
          }
        }

        // Normalize client NIPs and filter
        const clientsWithNorm = clientsList.map((c) => ({
          ...c,
          __norm_nip: normalizeNip(c.nip ?? c.NIP ?? c.kontrahentNip),
        }));

        let filteredClients = clientsWithNorm.filter((c) =>
          nipsWithTransactions.has(c.__norm_nip)
        );

        if (filteredClients.length === 0) {
          const exampleClientNips = clientsWithNorm
            .slice(0, 10)
            .map((c) => c.__norm_nip);
          console.warn("[Klienci] Brak wspólnego NIP po normalizacji.", {
            exampleClientNips,
          });
          if (salesData.length) {
            const sample = salesData[0];
            console.warn("[Klienci] Przykładowe klucze sprzedaży:", Object.keys(sample || {}));
          }
        }

        // Remove helper field before setting state
        filteredClients = filteredClients.map(({ __norm_nip, ...rest }) => rest);

        setData(filteredClients);

        if (filteredClients.length) {
          setColumns(buildColumns(filteredClients));
        } else if (clientsList.length) {
          setColumns(baseColumns);
        }
      } catch (err) {
        console.error("Błąd podczas ładowania danych:", err);
        setData([]);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ----------------- filters, paging, sorting ----------------- */

  const handleFilterChange = (field, val) => {
    setFilters((f) => ({ ...f, [field]: val }));
    setCurrentPage(1);
  };

  const filteredData = data.filter((item) =>
    ["nip", "nazwa", "wojewodztwo"].every((f) =>
      filters[f] === ""
        ? true
        : (item[f] ?? "")
            .toString()
            .toLowerCase()
            .includes(filters[f].toLowerCase())
    )
  );

  const totalPages = Math.ceil(filteredData.length / recordsPerPage) || 1;
  const currentRecords = filteredData.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  const handleSort = (col) => {
    const direction =
      sortConfig.key === col && sortConfig.direction === "ascending"
        ? "descending"
        : "ascending";
    setSortConfig({ key: col, direction });
    const sorted = [...data].sort((a, b) => {
      const av = (a[col] ?? "").toString();
      const bv = (b[col] ?? "").toString();
      if (av < bv) return direction === "ascending" ? -1 : 1;
      if (av > bv) return direction === "ascending" ? 1 : -1;
      return 0;
    });
    setData(sorted);
  };

  const getArrow = (col) =>
    sortConfig.key === col
      ? sortConfig.direction === "ascending"
        ? " ▲"
        : " ▼"
      : "";

  /* ----------------- Excel export ----------------- */

  // Export only the currently visible page
  const handleExportPage = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      currentRecords.map((row) =>
        columns.reduce(
          (acc, c) => ({
            ...acc,
            [columnLabels[c] || c]: row[c] ?? "-",
          }),
          {}
        )
      )
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, worksheet, "Klienci");
    XLSX.writeFile(wb, "Klienci_strona.xlsx");
  };

  // Export all records that match current filters (ignores pagination)
  const handleExportAll = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredData.map((row) =>
        columns.reduce(
          (acc, c) => ({
            ...acc,
            [columnLabels[c] || c]: row[c] ?? "-",
          }),
          {}
        )
      )
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, worksheet, "Klienci");
    XLSX.writeFile(wb, "Klienci_wszystkie_po_filtrach.xlsx");
  };

  /* ----------------- render ----------------- */

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <Sidebar />

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <TopBar />

        <HeaderContainer>
          <FaUsers />
          Kontrahenci / Dane
        </HeaderContainer>

        <Wrapper>
          <FilterBar>
            {["nip", "nazwa", "wojewodztwo"].map((f) => (
              <FilterGroup key={f}>
                <label>{columnLabels[f]}:</label>
                <input
                  type="text"
                  value={filters[f]}
                  onChange={(e) => handleFilterChange(f, e.target.value)}
                />
              </FilterGroup>
            ))}

            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <ExportButton onClick={handleExportPage}>
                <FaFileExcel style={{ marginRight: "6px" }} />
                Eksportuj bieżącą stronę
              </ExportButton>
              <ExportButton onClick={handleExportAll}>
                <FaFileExcel style={{ marginRight: "6px" }} />
                Eksportuj wszystkie (po filtrach)
              </ExportButton>
            </div>
          </FilterBar>

          <ScrollContainer>
            <div style={{ overflowX: "auto" }}>
              <PaginationControls>
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  Pierwsza
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Poprzednia
                </button>
                <span>
                  Strona {currentPage} z {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Następna
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Ostatnia
                </button>
              </PaginationControls>

              <StyledTable>
                <thead>
                  <TableRow>
                    {columns.map((col, i) => (
                      <TableHeader key={i} onClick={() => handleSort(col)}>
                        {columnLabels[col] || col}
                        {getArrow(col)}
                      </TableHeader>
                    ))}
                  </TableRow>
                </thead>
                <tbody>
                  {currentRecords.map((row, ri) => (
                    <TableRow key={ri}>
                      {columns.map((col, ci) => (
                        <TableCell key={ci}>{row[col] ?? "-"}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </tbody>
              </StyledTable>
            </div>
          </ScrollContainer>
        </Wrapper>
      </div>
    </div>
  );
};

export default KlienciComponent;
